import { useEffect, useState } from "react";

import Button from "@/components/Button";
import CheckboxCard from "@/components/Checkbox/CheckboxCard";
import Chip, { ChipSet } from "@/components/Chip";
import Radio from "@/components/Radio";
import RankedSelection from "@/components/RankedSelection";
import TextField from "@/components/TextField";
import {
  CAREER_INTENT_OPTIONS,
  CLARITY_OPTIONS,
  FACILITY_TYPE_OPTIONS,
  getOtherPositions,
  getRoleCode,
  getRoleLabel,
  getRoleOptionLabel,
  getSuggestedPositions,
  IMPROVEMENT_AREA_OPTIONS,
  MOTIVATION_OPTIONS,
  RETIREMENT_TYPE_OPTIONS,
  TIMEFRAME_OPTIONS,
  type CareerIntentOption
} from "./careerIntentCatalog";
import {
  moveRankedSelection,
  RANKED_POSITION_HINT,
  RANKED_POSITION_SUGGESTED_HINT,
  RANKED_SELECTION_HINT,
  rankedSelectionIndex,
  toggleCareerIntent,
  toggleRankedSelection,
  type CareerIntentFormValues,
  type CareerIntentStepId,
  type RetirementType,
  type Timeframe
} from "./careerIntentForm";
import CareerIntentStatePicker from "./_CareerIntentStatePicker";

export default function CareerGoalsQuestionField({
  currentRole,
  disabled = false,
  errorMessage,
  onChange,
  step,
  values
}: {
  currentRole?: string | null;
  disabled?: boolean;
  errorMessage?: string;
  onChange: (values: CareerIntentFormValues) => void;
  step: CareerIntentStepId;
  values: CareerIntentFormValues;
}) {
  switch (step) {
    case "intents":
      return (
        <div className="flex flex-col gap-6">
          <p className="text-body-large">Select all that apply.</p>
          <ErrorMessage message={errorMessage} />
          <div className="flex flex-col gap-3">
            {CAREER_INTENT_OPTIONS.map((option) => {
              const selected = values.selectedIntents.includes(option.value);

              return (
                <CheckboxCard
                  key={option.value}
                  checked={selected}
                  disabled={disabled}
                  headline={option.label}
                  name={`career-intent-intent-${option.value}`}
                  supportingText={option.hint}
                  testId={`career-intent-intent-${option.value}`}
                  onChange={() => onChange(toggleCareerIntent(values, option.value))}
                />
              );
            })}
          </div>
        </div>
      );

    case "improvement_areas":
      return (
        <div className="flex flex-col gap-6">
          <ErrorMessage message={errorMessage} />
          <OptionList
            disabled={disabled}
            maxSelections={3}
            onChange={(improvementAreas) => onChange({ ...values, improvementAreas })}
            options={IMPROVEMENT_AREA_OPTIONS}
            selected={values.improvementAreas}
            testIDPrefix="career-intent-improvement-area"
          />
        </div>
      );

    case "target_positions":
      return (
        <TargetPositionField
          currentRole={currentRole}
          disabled={disabled}
          errorMessage={errorMessage}
          onChange={onChange}
          values={values}
        />
      );

    case "locations":
      return (
        <div className="flex flex-col gap-6">
          <ErrorMessage message={errorMessage} />
          <CareerIntentStatePicker
            disabled={disabled}
            onChange={(locations) => onChange({ ...values, locations })}
            value={values.locations}
          />
        </div>
      );

    case "facility_types":
      return (
        <div className="flex flex-col gap-6">
          <ErrorMessage message={errorMessage} />
          <OptionList
            disabled={disabled}
            maxSelections={3}
            onChange={(facilityTypes) => onChange({ ...values, facilityTypes })}
            options={FACILITY_TYPE_OPTIONS}
            selected={values.facilityTypes}
            testIDPrefix="career-intent-facility-type"
          />
        </div>
      );

    case "retirement_type":
      return (
        <div className="flex flex-col gap-6">
          <ErrorMessage message={errorMessage} />
          <div className="flex flex-col gap-4" role="radiogroup" aria-label="Retirement Type">
            {RETIREMENT_TYPE_OPTIONS.map((option) => (
              <div data-testid={`career-intent-retirement-${option.value}`} key={option.value}>
                <Radio
                  checked={values.retirementType === option.value}
                  disabled={disabled}
                  id={`career-intent-retirement-${option.value}`}
                  label={option.label}
                  name="retirementType"
                  supportingText={option.hint}
                  onChange={(checked) => {
                    if (!checked) return;
                    onChange({ ...values, retirementType: option.value as RetirementType });
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      );

    case "motivations":
      return (
        <div className="flex flex-col gap-6">
          <ErrorMessage message={errorMessage} />
          <OptionList
            disabled={disabled}
            maxSelections={3}
            onChange={(motivations) => onChange({ ...values, motivations })}
            options={MOTIVATION_OPTIONS}
            selected={values.motivations}
            testIDPrefix="career-intent-motivation"
          />
        </div>
      );

    case "timing":
      return (
        <div className="flex flex-col gap-6">
          <h3 className="text-title-medium">When do you hope this happens?</h3>
          <ErrorMessage message={errorMessage} />
          <div className="flex flex-col gap-3" role="radiogroup" aria-label="When do you hope this happens?">
            {TIMEFRAME_OPTIONS.map((option) => (
              <Radio
                key={option.value}
                checked={values.timeframe === option.value}
                disabled={disabled}
                id={`career-intent-timeframe-${option.value}`}
                label={option.label}
                name="timeframe"
                onChange={(checked) => {
                  if (!checked) return;
                  onChange({ ...values, timeframe: option.value as Timeframe });
                }}
              />
            ))}
          </div>
          <h3 className="text-title-medium">How clear are you on your next steps?</h3>
          <div className="flex flex-col gap-3" role="radiogroup" aria-label="How clear are you on your next steps?">
            {CLARITY_OPTIONS.map((option) => (
              <Radio
                key={option.value}
                checked={values.clarity === Number(option.value)}
                disabled={disabled}
                id={`career-intent-clarity-${option.value}`}
                label={option.label}
                name="clarity"
                onChange={(checked) => {
                  if (!checked) return;
                  onChange({ ...values, clarity: Number(option.value) });
                }}
              />
            ))}
          </div>
        </div>
      );

    case "notes":
      return (
        <div className="w-full min-w-0" data-testid="career-intent-additional-context">
          <TextField
            className="w-full"
            disabled={disabled}
            label="Additional Context"
            onChangeText={(additionalContext) => onChange({ ...values, additionalContext })}
            placeholder="e.g. dream clubs, non-negotiables, other constraints, etc"
            rows={12}
            type="textarea"
            value={values.additionalContext}
          />
        </div>
      );
    default: {
      const _exhaustive: never = step;
      return _exhaustive;
    }
  }
}

function rankedOptionLabel(label: string, rank?: number) {
  return rank != null ? `${rank}. ${label}` : label;
}

function OptionList({
  disabled = false,
  maxSelections,
  onChange,
  options,
  selected,
  testIDPrefix
}: {
  disabled?: boolean;
  maxSelections?: number;
  onChange: (value: string[]) => void;
  options: CareerIntentOption[];
  selected: string[];
  testIDPrefix: string;
}) {
  const atMaxSelections = maxSelections != null && selected.length >= maxSelections;
  const rankedItems = selected.flatMap((value) => {
    const option = options.find((item) => item.value === value);
    return option ? [{ value, label: option.label }] : [];
  });

  return (
    <div className="flex flex-col gap-6">
      {maxSelections ? (
        <p className="text-body-large text-sys-brand-on-surface-variant">{RANKED_SELECTION_HINT}</p>
      ) : null}
      <ChipSet>
        {options.map((option) => {
          const isSelected = selected.includes(option.value);
          const rank = rankedSelectionIndex(selected, option.value);

          return (
            <span data-testid={`${testIDPrefix}-${option.value}`} key={option.value}>
              <Chip
                disabled={Boolean(disabled || (atMaxSelections && !isSelected))}
                label={rankedOptionLabel(option.label, rank)}
                selected={isSelected}
                onClick={() =>
                  onChange(toggleRankedSelection(selected, option.value, maxSelections ?? Number.MAX_SAFE_INTEGER))
                }
              />
            </span>
          );
        })}
      </ChipSet>
      <RankedSelection
        disabled={disabled}
        items={rankedItems}
        onMove={(index, direction) => onChange(moveRankedSelection(selected, index, direction))}
        onRemove={(value) => onChange(toggleRankedSelection(selected, value, maxSelections ?? Number.MAX_SAFE_INTEGER))}
        testIdPrefix={testIDPrefix}
      />
    </div>
  );
}

function ErrorMessage({ message }: { message?: string }) {
  if (!message) return null;

  return (
    <p aria-live="polite" className="text-body-medium text-sys-brand-error" data-testid="career-intent-question-error">
      {message}
    </p>
  );
}

function TargetPositionField({
  currentRole,
  disabled = false,
  errorMessage,
  onChange,
  values
}: {
  currentRole?: string | null;
  disabled?: boolean;
  errorMessage?: string;
  onChange: (values: CareerIntentFormValues) => void;
  values: CareerIntentFormValues;
}) {
  const [showOtherPositions, setShowOtherPositions] = useState(false);
  const currentRoleCode = getRoleCode(currentRole);
  const hasCurrentRole = Boolean(currentRoleCode);
  const suggestedPositions = hasCurrentRole ? getSuggestedPositions(currentRoleCode) : [];
  const otherPositions = getOtherPositions(currentRoleCode);

  useEffect(() => {
    if (!hasCurrentRole) return;
    if (values.targetPositions.some((position) => otherPositions.some((option) => option.value === position))) {
      setShowOtherPositions(true);
    }
  }, [hasCurrentRole, otherPositions, values.targetPositions]);

  return (
    <div>
      {errorMessage ? (
        <div className="mb-6">
          <ErrorMessage message={errorMessage} />
        </div>
      ) : null}
      <p className="mb-6 text-body-large text-sys-brand-on-surface-variant">
        {hasCurrentRole && suggestedPositions.length > 0 ? RANKED_POSITION_SUGGESTED_HINT : RANKED_POSITION_HINT}
      </p>
      <RankedSelection
        className="mb-8"
        disabled={disabled}
        items={values.targetPositions.map((value) => ({
          value,
          label: getRoleLabel(value) ?? value
        }))}
        onMove={(index, direction) =>
          onChange({ ...values, targetPositions: moveRankedSelection(values.targetPositions, index, direction) })
        }
        onRemove={(value) =>
          onChange({
            ...values,
            targetPositions: toggleRankedSelection(values.targetPositions, value)
          })
        }
        testIdPrefix="career-intent-target-position"
      />
      <div className="flex flex-col gap-6">
        {hasCurrentRole && suggestedPositions.length > 0 ? (
          <div className="flex flex-col gap-3">
            {suggestedPositions.map((option) => (
              <PositionOption
                disabled={disabled}
                key={option.value}
                onChange={(targetPositions) => onChange({ ...values, targetPositions })}
                option={option}
                selected={values.targetPositions}
              />
            ))}
          </div>
        ) : null}
        {hasCurrentRole ? (
          <>
            <div className="self-start">
              <Button
                type="button"
                variant="text"
                ariaExpanded={showOtherPositions}
                ariaLabel="Choose a Different Position"
                testId="career-intent-toggle-other-positions"
                disabled={disabled}
                icon={showOtherPositions ? "expand_less" : "expand_more"}
                trailingIcon
                onClick={() => setShowOtherPositions((current) => !current)}
              >
                Choose a Different Position
              </Button>
            </div>
            {showOtherPositions ? (
              <div className="flex flex-col gap-3">
                {otherPositions.map((option) => (
                  <PositionOption
                    disabled={disabled}
                    key={option.value}
                    onChange={(targetPositions) => onChange({ ...values, targetPositions })}
                    option={option}
                    selected={values.targetPositions}
                  />
                ))}
              </div>
            ) : null}
          </>
        ) : (
          <div className="flex flex-col gap-3">
            {otherPositions.map((option) => (
              <PositionOption
                disabled={disabled}
                key={option.value}
                onChange={(targetPositions) => onChange({ ...values, targetPositions })}
                option={option}
                selected={values.targetPositions}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PositionOption({
  disabled = false,
  onChange,
  option,
  selected
}: {
  disabled?: boolean;
  onChange: (value: string[]) => void;
  option: { value: string; label: string };
  selected: string[];
}) {
  const isSelected = selected.includes(option.value);
  const optionDisabled = disabled || (!isSelected && selected.length >= 3);
  const rank = rankedSelectionIndex(selected, option.value);

  return (
    <CheckboxCard
      checked={isSelected}
      disabled={optionDisabled}
      headline={getRoleOptionLabel(option)}
      name={`career-intent-target-position-${option.value.toLowerCase()}`}
      rank={rank}
      testId={`career-intent-target-position-${option.value.toLowerCase()}`}
      onChange={() => onChange(toggleRankedSelection(selected, option.value))}
    />
  );
}
